<template>
  <div v-if="show"
       class="fixed inset-0 bg-gray-900 bg-opacity-60 overflow-y-hidden h-full w-full flex items-center justify-center text-base">
    <div class="bg-white rounded-lg shadow-xl w-11/12 h-[88vh] overflow-hidden">
      <div class="flex items-center justify-between p-4 border-b">
        <h2 class="text-2xl text-gray-800 font-semibold">Radiology</h2>
        <button @click="closeModal" class="text-gray-500 hover:text-gray-700" aria-label="Close">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <div class="p-6 flex h-[75vh] overflow-y-auto">
        <!-- Left column -->
        <div class="w-2/12 pr-6 border-r">
          <div class="flex items-center mb-4">
            <img src="@/assets/images/avatar.png" alt="Patient" class="w-12 h-12 rounded-full mr-4" />
            <div>
              <h3 class="font-semibold">{{ patient?.FullName }}</h3>
              <p class="text-md text-gray-600">Visit Code: {{ visitCode }}</p>
            </div>
          </div>

          <div class="space-y-2 mb-6 border-b-2">
            <p><span class="font-semibold">Patient Unique Code:</span> {{ patient?.unique_code }}</p>
            <p><span class="font-semibold">Email:</span> {{ patient?.EmailAddress }}</p>
            <p><span class="font-semibold">Phone:</span> {{ patient?.PhoneNumber }}</p>
            <p><span class="font-semibold">D.O.B:</span> {{ patient?.Dob }}</p>
            <p><span class="font-semibold">Gender:</span> {{ patient?.Gender }}</p>
            <p><span class="font-semibold">Emergency Contact:</span> {{ patientDetails.emergencyContact }}</p>
            <p><span class="font-semibold">Phone:</span> {{ patientDetails.emergencyPhone }}</p>
          </div>

          <h4 class="font-semibold mb-2">Vitals History</h4>
          <div class="grid grid-cols-3 gap-4">
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <path d="M10 13.5a4 4 0 1 0 4 0v-8.5a2 2 0 0 0 -4 0v8.5" />
                  <line x1="10" y1="9" x2="14" y2="9" />
                </svg>
              </span>
              <p class="text-xs">Temperature</p>
              <p class="font-semibold">{{ currentVitals?.temperature }}°C</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </span>
              <p class="text-xs">Heart Rate</p>
              <p class="font-semibold">{{ currentVitals?.heart_rate }}BPM</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="dark:custom-yellow text-xl">🫁</span>
              <p class="text-xs">Respiratory</p>
              <p class="font-semibold">{{ currentVitals?.respiratory_rate || '-' }}</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-xl">🩸</span>
              <p class="text-xs">Blood Pressure</p>
              <p class="font-semibold">{{ currentVitals?.bloodPressure }}</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <line x1="12" y1="3" x2="12" y2="21" />
                  <line x1="9" y1="21" x2="15" y2="21" />
                  <polyline points="3 6 6 7 12 5 18 7 21 6" />
                  <path d="M6 7l-3 9a5 5 0 0 0 6 0l-3 -9" />
                  <path d="M18 7l-3 9a5 5 0 0 0 6 0l-3 -9" />
                </svg>
              </span>
              <p class="text-xs">Weight</p>
              <p class="font-semibold">{{ currentVitals?.weight }}KG</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <path d="M3 21h4l13 -13a1.5 1.5 0 0 0 -4 -4l-13 13v4" />
                  <line x1="14.5" y1="5.5" x2="18.5" y2="9.5" />
                  <polyline points="12 8 7 3 3 7 8 12" />
                  <line x1="7" y1="8" x2="5.5" y2="9.5" />
                  <polyline points="16 12 21 17 17 21 12 16" />
                  <line x1="16" y1="17" x2="14.5" y2="18.5" />
                </svg>
              </span>
              <p class="text-xs">Height</p>
              <p class="font-semibold">{{ currentVitals?.height }} cm</p>
            </div>
          </div>
          <div class="mt-4 grid grid-cols-2 gap-4">
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <path d="M12 9v12m-8 -8a8 8 0 0 0 16 0m1 0h-2m-14 0h-2" />
                  <circle cx="12" cy="6" r="3" />
                </svg>
              </span>
              <p class="text-xs">BMI</p>
              <p class="font-semibold">{{ bmi }} KG/m2</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <circle cx="12" cy="5" r="2" />
                  <path d="M10 22v-5l-1-1v-4a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4l-1 1v5" />
                </svg>
              </span>
              <p class="text-xs">BSA</p>
              <p class="font-semibold">{{ bsa }} m2</p>
            </div>
          </div>
        </div>

        <!-- Right column -->
        <div class="w-10/12 pl-6">
          <div v-if="isRejection" class="tabs flex border-b mb-4">
            <button class="px-1 py-2 -mb-px font-semibold border-b-2 px-4"
                    :class="{ 'border-custom-yellow text-custom-blue': currentTab === 'rejection-reason', 'text-gray-500': currentTab !== 'rejection-reason' }"
                    @click="currentTab = 'rejection-reason'">
              Indicate a reason for rejecting {{ radiologyTest.imaging_type.name }}
            </button>
          </div>
          <div v-else-if="isSchedule" class="tabs flex border-b mb-4">
            <button class="px-1 py-2 -mb-px font-semibold border-b-2 px-4"
                    :class="{ 'border-custom-yellow text-custom-blue': currentTab === 'schedule-radiology', 'text-gray-500': currentTab !== 'schedule-radiology' }"
                    @click="currentTab = 'schedule-radiology'">
              Schedule Radiology
            </button>
          </div>
          <div v-else class="tabs flex border-b mb-4">
            <button class="px-1 py-2 -mb-px font-semibold border-b-2 px-4"
                    :class="{ 'border-custom-yellow text-custom-blue': currentTab === 'conduct-exam', 'text-gray-500': currentTab !== 'conduct-exam' }"
                    @click="currentTab = 'conduct-exam'">
              Conduct Imaging Exam
            </button>
            <button class="px-1 py-2 -mb-px font-semibold border-b-2 px-4"
                    :class="{ 'border-custom-yellow text-custom-blue': currentTab === 'review-results', 'text-gray-500': currentTab !== 'review-results' }"
                    @click="currentTab = 'review-results'">
              Review Results
            </button>
            <button class="px-1 py-2 -mb-px font-semibold border-b-2 px-4"
                    :class="{ 'border-custom-yellow text-custom-blue': currentTab === 'send-results', 'text-gray-500': currentTab !== 'send-results' }"
                    @click="currentTab = 'send-results'">
              Published Results
            </button>
          </div>
          <component :is="currentTabComponent"
                     :radiologyTest="radiologyTest"
                     :requests="radiologyTests"
                     ref="currentTabComponent"
                     :search-query="searchQuery"
                     @refetch-requests="$emit('refetch-requests')"
                     @mounted="logTabMounted">
          </component>
        </div>
      </div>

      <div class="flex justify-end space-x-2 py-3 px-4 border-t">
        <button @click="closeModal" class="px-8 py-1 bg-gray-200 text-gray-800 rounded">
          Cancel
        </button>
        <template v-if="currentTab === 'rejection-reason'">
          <button @click="handleRejection" class="px-8 py-1 bg-custom-yellow text-white rounded" :disabled="showRejectionLoader">
            {{ showRejectionLoader ? "Rejecting..." : "Reject" }}
          </button>
        </template>
        <template v-if="currentTab === 'schedule-radiology'">
          <button @click="handleNextTab" class="px-8 py-1 bg-custom-yellow text-white rounded">
            Finish
          </button>
        </template>
        <template v-if="currentTab === 'conduct-exam'">
          <button @click="handleSubmitRadiologyTest" class="px-8 py-1 bg-custom-yellow text-white rounded">
            Next
          </button>
        </template>
        <template v-if="currentTab === 'review-results'">
          <button @click="handleSubmitRadiologyTest" class="px-8 py-1 bg-custom-yellow text-white rounded">
            Next
          </button>
        </template>
        <template v-if="currentTab === 'send-results'">
          <button @click="handleSubmitRadiologyTest" class="px-8 py-1 bg-custom-yellow text-white rounded">
            Finish
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import RadiologyRejectionTab from '@/views/tabs/radiology_tabs/RadiologyRejectionTab.vue';
import ScheduleRadiologyTab from '@/views/tabs/radiology_tabs/ScheduleRadiologyTab.vue';
import ConductExamTab from '@/views/tabs/radiology_tabs/ConductExamTab.vue';
import ReviewResultsTab from '@/views/tabs/radiology_tabs/ReviewResultsTab.vue';
import SendResultsTab from '@/views/tabs/radiology_tabs/SendResultsTab.vue';
import apiClient from '@/services/api';

export default {
  components: {
    RadiologyRejectionTab,
    ScheduleRadiologyTab,
    ConductExamTab,
    ReviewResultsTab,
    SendResultsTab,
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    patientId: {
      type: String,
      required: true
    },
    radiologyTest: {
      type: Object,
      required: true
    },
    patient: {
      type: Object,
      required: true
    },
    visitCode: {
      type: String,
      required: true
    },
    radiologyTests: {
      type: Array,
      required: true
    },
    isSchedule: {
      type: Boolean,
      default: false
    },
    isRejection: {
      type: Boolean,
      default: false
    },
    currentVitals: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentTab: this.isRejection ? 'rejection-reason' : (this.isSchedule ? 'schedule-radiology' : 'conduct-exam'),
      tabs: this.isRejection
        ? ['rejection-reason']
        : (this.isSchedule
          ? ['schedule-radiology']
          : ['conduct-exam', 'review-results', 'send-results', 'notification-alert']),
      showRejectionLoader: false,
      patientDetails: {
        emergencyContact: 'Moses Kinyua',
        emergencyPhone: '0745765463'
      }
    };
  },
  mounted() {
    this.currentTab = this.isRejection ? 'rejection-reason' : (this.isSchedule ? 'schedule-radiology' : 'conduct-exam');
  },
  watch: {
    isRejection(newValue) {
      this.currentTab = newValue ? 'rejection-reason' : 'conduct-exam';
    }
  },
  computed: {
    currentTabComponent() {
      if (this.isRejection) {
        switch (this.currentTab) {
          case 'rejection-reason':
            return 'RadiologyRejectionTab';
          default:
            return 'RadiologyRejectionTab';
        }
      } else if (this.isSchedule) {
        switch (this.currentTab) {
          case 'schedule-radiology':
            return 'ScheduleRadiologyTab';
          default:
            return 'ScheduleRadiologyTab';
        }
      } else {
        switch (this.currentTab) {
          case 'conduct-exam':
            return 'ConductExamTab';
          case 'review-results':
            return 'ReviewResultsTab';
          case 'send-results':
            return 'SendResultsTab';
          default:
            return 'ConductExamTab';
        }
      }
    },
    bmi() {
      const heightInMeters = this.currentVitals?.height / 100; // Convert cm to meters
      const weightInKg = this.currentVitals?.weight;
      return (weightInKg / (heightInMeters * heightInMeters)).toFixed(1);
    },
    bsa() {
      const heightInCm = this.currentVitals?.height;
      const weightInKg = this.currentVitals?.weight;
      return Math.sqrt((heightInCm * weightInKg) / 3600).toFixed(1);
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    handleNextTab() {
      const currentIndex = this.tabs.indexOf(this.currentTab);
      if (currentIndex < this.tabs.length - 1) {
        this.currentTab = this.tabs[currentIndex + 1];
      } else {
        this.$emit('submit', this.patientId);
      }
    },
    handleSubmitRadiologyTest() {
      if (this.currentTab === 'send-results') {
        this.closeModal();
        this.$router.push('/radiology');
        return;
      }
      this.handleNextTab();
    },
    async handleRejection() {
      const currentTabRef = this.$refs.currentTabComponent;
      let rejectionForm = '';
      if (currentTabRef && currentTabRef.labForm) {
        rejectionForm = currentTabRef.labForm;
      }

      const payload = {
        id: this.radiologyTest.id,
        rejection_reason: rejectionForm.reason,
        facilityId: this.patient.FacilityId,
        userId: this.radiologyTest.requested_by.id
      };
      try {
        this.showRejectionLoader = true;
        const response = await apiClient.rejectRadiologyTest(payload);
        if (response.success) {
          this.$toast.success('Radiology test rejected successfully.');
          this.$emit('refetch-requests');
          this.$emit('close');
        } else {
          this.$toast.error('Failed to reject radiology test.');
        }
      } catch (error) {
        this.$toast.error('Failed to reject radiology test: ' + error.message);
        console.error('Rejection error:', error);
      } finally {
        this.showRejectionLoader = false;
      }
    },
    logTabMounted(tabName) {
      console.log(`${tabName} component mounted`);
    }
  }
};
</script>